import React, { useMemo, useState, useCallback, memo } from "react";
import { isEmpty } from "lodash-es";

import { copy } from "shared/assets";
import { FormBuilder, Map, Message } from "shared/components";
import { Spacer } from "shared/layout";
import { IAddress, ICoordinate, IStatus } from "shared/types";
import { MapContainer } from "./styles";
import { getFields, buttons } from "./formData";
import { GeocodingService } from "shared/services";
import {
  DEFAULT_COORDINATES,
  DEFAULT_FOCUS_ZOOM,
  DEFAULT_ZOOM,
} from "shared/components/Map/utils/constants";
import { useScrollIntoView } from "Venues/utils";

interface AddressFormProps {
  submitAddress?: (address: IAddress | null) => void;
  blurAddress?: (address: IAddress | null) => void;
  defaultValues?: IAddress | Record<string, never>;
}

export const AddressForm: React.FC<AddressFormProps> = memo(
  ({ submitAddress, blurAddress, defaultValues }) => {
    const [message, setMessage] = useState<string | null>();
    const [address, setAddress] = useState<IAddress | null>(
      !isEmpty(defaultValues) ? (defaultValues as IAddress) : null
    );
    const [mapZoom, setMapZoom] = useState<number | null>(
      !isEmpty(defaultValues) ? DEFAULT_FOCUS_ZOOM : DEFAULT_ZOOM
    );
    const [loadingGeocoding, setLoadingGeocoding] = useState(false);
    const [tempAddress, setTempAddress] = useState<Partial<IAddress>>(address || {});

    const checkAddressValid = useCallback((address: Partial<IAddress>): boolean => {
      const { addressLine1, city, zip, country } = address;

      return (
        (addressLine1 &&
          addressLine1 !== "" &&
          city &&
          city !== "" &&
          zip &&
          zip !== "" &&
          country &&
          country !== "") ||
        false
      );
    }, []);

    const addressIsValid = useMemo(
      () => checkAddressValid(tempAddress),
      [tempAddress, checkAddressValid]
    );

    const { ref, scrollIntoView } = useScrollIntoView();

    const coordinates: ICoordinate | undefined = useMemo(() => {
      const { longitude, latitude } = address || {};
      if (typeof longitude === "number" && typeof latitude === "number") {
        return {
          latitude,
          longitude,
        };
      }
    }, [address]);

    const handleChange = useCallback(
      ({ value, name }: { value: string; name: string }) => {
        const newAddress = { ...tempAddress, [name]: value };
        setTempAddress(newAddress);

        if (checkAddressValid(newAddress)) {
          setMessage(copy.createVenue.messages.confirmAddress);
        } else {
          setMessage(null);
        }
      },
      [tempAddress, setTempAddress, checkAddressValid]
    );

    const fields = useMemo(() => getFields(handleChange), [handleChange]);

    const handleSubmit = async (address: IAddress) => {
      setMessage(null);
      setLoadingGeocoding(true);

      let coordinates: ICoordinate = DEFAULT_COORDINATES;
      try {
        coordinates = await GeocodingService.get(address);
        setMapZoom(DEFAULT_FOCUS_ZOOM);
      } catch (error) {
        setMessage(copy.createVenue.messages.unknownAddress);
        setMapZoom(DEFAULT_ZOOM);
      }
      setLoadingGeocoding(false);
      scrollIntoView(true);
      handleSubmitAddress({ ...address, ...coordinates });
    };

    const handleSubmitAddress = (address: IAddress) => {
      setAddress(address);
      submitAddress && submitAddress(address);
    };

    const updateCoordinates = (coordinates: ICoordinate) => {
      if (address) {
        setMapZoom(null);
        handleSubmitAddress({ ...address, ...coordinates });
      }
    };

    return (
      <React.Fragment>
        <Spacer size="l">
          <FormBuilder
            fields={fields}
            buttons={buttons}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            onBlur={blurAddress}
            style={{
              gridTemplateColumns: "1fr 1fr",
              mobile: { gridTemplateColumns: "1fr" },
            }}
            isSubmitting={loadingGeocoding}
            preventSubmit={!addressIsValid}
          />

          {!!message && <Message message={message} status={IStatus.warning} />}
          <MapContainer ref={ref}>
            <Map
              coordinates={coordinates}
              draggable={{
                setCoordinates: updateCoordinates,
                tooltip: copy.createVenue.messages.dragMarker,
              }}
              zoom={mapZoom}
            />
          </MapContainer>
        </Spacer>
      </React.Fragment>
    );
  }
);

AddressForm.displayName = "AddressForm";
